/**
 * useDashboardStats Hook
 *
 * Fetches dashboard statistics with period/groupBy controls.
 */

import { useState, useEffect, useCallback } from 'react';
import { getDashboardStats, DashboardStatsResponse } from '../services/documentApi';

interface UseDashboardStatsResult {
    data: DashboardStatsResponse | null;
    loading: boolean;
    error: string | null;
    refetch: () => void;
}

export const useDashboardStats = (
    period: string = '30d',
    groupBy: string = 'day',
    startDate?: string,
    endDate?: string,
): UseDashboardStatsResult => {
    const [data, setData] = useState<DashboardStatsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await getDashboardStats(period, groupBy, startDate, endDate);
            if (response.success && response.data) {
                setData(response.data);
            } else {
                setError(response.message || 'Failed to fetch stats');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Network error');
        } finally {
            setLoading(false);
        }
    }, [period, groupBy, startDate, endDate]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    return { data, loading, error, refetch: fetchStats };
};

export default useDashboardStats;
